import { createContext, useContext } from 'react';

import type { AuthProfile } from './parseProfile';

/**
 * `appState` round-trips through Auth0's universal login: `login` sends
 * `returnTo`, and the provider's `onRedirectCallback` receives it back after
 * the callback exchange.
 */
export interface AuthAppState {
  returnTo?: string;
}

/**
 * `unauthed` vs `signedout` is the difference between "we found no session"
 * and "we deliberately dropped this tab's session", and only the first may
 * auto-start a login (the legacy portal's PLA-511 lesson: multiple tabs
 * racing into /authorize destroy each other's per-origin transaction).
 *
 * `error` is the third distinction and the one that matters most for guards:
 * either the SDK's own exchange failed, or the session is live but its token
 * could not be turned into a profile — a rejected audience, a fatal
 * classified code, or claims `parseProfile` refuses. A fresh login cannot fix
 * any of that, so `error` must NEVER be reported as `unauthed`: that is the
 * status `useAuthGate` redirects on, and /authorize would hand back an
 * identically broken token. It is terminal, and `error` on `AuthState`
 * carries the reason.
 */
export type AuthStatus = 'loading' | 'authed' | 'unauthed' | 'signedout' | 'error';

export type TokenGetter = () => Promise<string>;

export interface AuthState {
  status: AuthStatus;
  /** Present only when `status === 'authed'`. */
  profile?: AuthProfile;
  /**
   * The SDK's exchange error, or a captured classified failure (e.g. a zod
   * message) — whichever settled `status: 'error'`.
   */
  error?: Error;
  /** Starts the universal-login redirect, carrying `returnTo` via appState. */
  login: (options?: { returnTo?: string }) => Promise<void>;
  /** Full Auth0 logout (clears the Auth0 session, returns to origin). */
  logout: () => void;
  /**
   * Drop ONLY this tab's local session — no Auth0 round-trip, no navigation —
   * and settle on `status: 'signedout'`. Terminal for the tab; logging back
   * in goes through Auth0, i.e. a fresh page load.
   */
  signOutLocally: () => void;
  /**
   * Acquire an access token (B4 seam). Resolves the token, or throws:
   * `ReauthRequiredError` for the interaction-required class (the consumer
   * starts a login), transient/fatal errors pass through. Injectable as
   * `TokenGetter`. Throws without hitting the SDK once signed out locally.
   */
  getToken: TokenGetter;
}

export const AuthContext = createContext<AuthState | undefined>(undefined);

/**
 * The auth seam's state surface. Consumers — route guards, login/callback
 * pages — read auth state here, never from `@auth0/auth0-react` directly.
 * The B4 token seam extends this same boundary with token acquisition.
 */
export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return context;
}
